// src/consent_api.ts
var PERMISSION_VALUE = {
  ACCEPTED: "1",
  DENIED: "0",
  // user has not made a choice yet
  UNKNOWN: null
};
var resolveConsentApi = () => {
  if (window._scc_) {
    return {
      type: "scc",
      api: window._scc_
    };
  }
  if (window._tcf_) {
    return {
      type: "tcf",
      api: window._tcf_
    };
  }
  return void 0;
};

// src/tracker.ts
var noop = () => {
};
var getCachedValue = (category) => {
  try {
    return window.localStorage.getItem(category);
  } catch (e) {
    return null;
  }
};
var getStatus = (category) => {
  const value = getCachedValue(category);
  switch (value) {
    case "1":
      return "accepted" /* ACCEPTED */;
    case "0":
      return "rejected" /* REJECTED */;
    default:
      return "unknown" /* UNKNOWN */;
  }
};
var getCachedConsents = () => {
  const purposes = {
    ["CMP_ADVERTISING" /* CMP_ADVERTISING */]: {
      status: getStatus("CMP:advertising")
    },
    ["CMP_ANALYTICS" /* CMP_ANALYTICS */]: {
      status: getStatus("CMP:analytics")
    },
    ["CMP_PERSONALIZATION" /* CMP_PERSONALIZATION */]: {
      status: getStatus("CMP:personalisation")
    },
    ["CMP_MARKETING" /* CMP_MARKETING */]: {
      status: getStatus("CMP:marketing")
    }
  };
  if (Object.values(purposes).some((purpose) => purpose.status === "unknown" /* UNKNOWN */)) {
    return void 0;
  }
  return {
    purposes,
    source: "cache" /* CACHE */
  };
};
var getDefaultConsents = () => {
  return {
    purposes: {
      ["CMP_ADVERTISING" /* CMP_ADVERTISING */]: {
        status: "rejected" /* REJECTED */
      },
      ["CMP_ANALYTICS" /* CMP_ANALYTICS */]: {
        status: "rejected" /* REJECTED */
      },
      ["CMP_PERSONALIZATION" /* CMP_PERSONALIZATION */]: {
        status: "rejected" /* REJECTED */
      },
      ["CMP_MARKETING" /* CMP_MARKETING */]: {
        status: "rejected" /* REJECTED */
      }
    },
    source: "default" /* DEFAULT */
  };
};
var getCachedOrDefaultConsentsForPulse = (api) => {
  var _a, _b;
  const consentApi = api != null ? api : (_a = resolveConsentApi()) == null ? void 0 : _a.api;
  return (_b = consentApi == null ? void 0 : consentApi.getCachedOrDefaultConsentsForPulse()) != null ? _b : getCachedConsents();
};
var getConsentsForPulse = (api) => {
  var _a, _b;
  const consentApi = api != null ? api : (_a = resolveConsentApi()) == null ? void 0 : _a.api;
  if (!consentApi) {
    const consents = (_b = getCachedConsents()) != null ? _b : getDefaultConsents();
    return Promise.resolve(consents);
  }
  return new Promise((resolve) => {
    consentApi.getPermission("CMP:advertising", () => {
      const consents = consentApi.getCachedOrDefaultConsentsForPulse();
      resolve(consents);
    });
  });
};
var subscribeTrackerToConsents = (tracker, api) => {
  var _a;
  const consentApi = api != null ? api : (_a = resolveConsentApi()) == null ? void 0 : _a.api;
  if (!consentApi) {
    const consents = getCachedConsents();
    if (consents) {
      tracker.setConsents(consents);
    }
    return noop;
  }
  const setConsents = () => {
    const consents = consentApi.getCachedOrDefaultConsentsForPulse();
    tracker.setConsents(consents);
  };
  const unsubscribes = ["CMP:advertising", "CMP:analytics", "CMP:marketing", "CMP:personalisation"].map(
    (category) => {
      return consentApi.subscribe(category, setConsents);
    }
  );
  return () => {
    unsubscribes.forEach((unsubscribe) => unsubscribe());
  };
};

// src/consent_manager.ts
var noop2 = () => {
};
var ConsentManager = class {
  constructor({
    consentApi = resolveConsentApi(),
    allowlist
  }) {
    const { api, type } = consentApi || {};
    this.type = type != null ? type : "fallback";
    this.consentApi = api;
    this.allowlist = allowlist;
  }
  isCategorySupported(category) {
    return this.allowlist.includes(category);
  }
  getCachedOrDefaultConsentsForPulse() {
    return getCachedOrDefaultConsentsForPulse(this.consentApi);
  }
  getConsentsForPulse() {
    return getConsentsForPulse(this.consentApi);
  }
  getPermission(category, callback) {
    if (!this.consentApi || !this.isCategorySupported(category)) {
      callback(PERMISSION_VALUE.ACCEPTED);
    } else {
      this.consentApi.getPermission(category, callback);
    }
  }
  getPermissionSync(category) {
    if (!this.consentApi || !this.isCategorySupported(category)) {
      return PERMISSION_VALUE.ACCEPTED;
    }
    return this.consentApi.getPermissionSync(category);
  }
  getPermissionAsync(category) {
    return new Promise((resolve) => {
      this.getPermission(category, (value) => resolve(value));
    });
  }
  isConsentedToAll(callback) {
    if (!this.consentApi) {
      callback(true);
    } else {
      this.consentApi.isConsentedToAll(callback);
    }
  }
  getConsentedToAllSync() {
    if (!this.consentApi) {
      return true;
    } else {
      return this.consentApi.getConsentedToAllSync();
    }
  }
  getConsentedToAll() {
    return new Promise((resolve) => {
      const value = this.getConsentedToAllSync();
      if (typeof value === "boolean") {
        resolve(value);
      } else {
        this.isConsentedToAll(resolve);
      }
    });
  }
  notify(category, value) {
    var _a;
    (_a = this.consentApi) == null ? void 0 : _a.notify(category, value);
  }
  showPrivacyManager() {
    var _a;
    (_a = this.consentApi) == null ? void 0 : _a.showPrivacyManager();
  }
  subscribe(category, callback) {
    var _a, _b;
    return (_b = (_a = this.consentApi) == null ? void 0 : _a.subscribe(category, callback)) != null ? _b : noop2;
  }
  subscribeTrackerToConsents(tracker) {
    return subscribeTrackerToConsents(tracker, this.consentApi);
  }
};
export {
  ConsentManager,
  PERMISSION_VALUE,
  getCachedOrDefaultConsentsForPulse,
  getConsentsForPulse,
  subscribeTrackerToConsents
};
